import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import { SectionKey } from './use-settings'

export const SECTIONS: {
  key: SectionKey
  label: string
  icon: React.ReactNode
}[] = [
  { key: 'profile', label: 'Profile', icon: <PersonOutlineRoundedIcon /> },
  { key: 'preferences', label: 'Preferences', icon: <TuneRoundedIcon /> },
  {
    key: 'notifications',
    label: 'Notifications',
    icon: <MailOutlineRoundedIcon />,
  },
  { key: 'privacy', label: 'Privacy', icon: <ShieldOutlinedIcon /> },
  { key: 'data', label: 'Data & account', icon: <DownloadRoundedIcon /> },
]

const NavList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  position: 'sticky',
  top: theme.spacing(11),
}))

const NavItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.25, 1.75),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: active ? 600 : 500,
  color: active ? theme.palette.brand.main : theme.palette.text.primary,
  backgroundColor: active ? theme.palette.action.selected : 'transparent',
  border: `1px solid ${active ? theme.palette.divider : 'transparent'}`,
  transition: 'background-color 0.15s ease, color 0.15s ease',
  '& .MuiSvgIcon-root': {
    fontSize: 18,
    color: active ? theme.palette.brand.main : theme.palette.text.secondary,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}))

interface Props {
  active: SectionKey
  onSelect: (section: SectionKey) => void
}

export const SettingsNav: React.FC<Props> = ({ active, onSelect }) => (
  <NavList role='tablist' aria-orientation='vertical'>
    {SECTIONS.map((section) => (
      <NavItem
        key={section.key}
        role='tab'
        tabIndex={0}
        aria-selected={active === section.key}
        active={active === section.key}
        onClick={() => onSelect(section.key)}
        onKeyDown={(event: React.KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onSelect(section.key)
          }
        }}
      >
        {section.icon}
        <Typography component='span' fontSize={14} fontWeight='inherit'>
          {section.label}
        </Typography>
      </NavItem>
    ))}
  </NavList>
)

export default SettingsNav
